// src/helpers/axiosInstance.js
import axios from "axios";
import Cookies from "js-cookie";
import { toast } from "react-toastify";
import { errorToastShownSince } from "../../utils/toastSetup";
const axiosInstance = axios.create({
    baseURL: process.env.REACT_APP_API_URL || `https://bds-pre-prod.accruewise.in/api/v1/`,
});

// Get current timezone
const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

// Request Interceptor
axiosInstance.interceptors.request.use(
    async (config) => {
        if (typeof window !== "undefined") {
            // Get locale from cookie
            const locale =
                document.cookie
                    .split("; ")
                    .find((row) => row.startsWith("NEXT_LOCALE="))
                    ?.split("=")[1] || "en";

            // Get token
            const token = Cookies.get("accruewise-token");

            if (config.headers) {
                // Attach Authorization only when token exists
                // admin/register creates a staff account and needs the
                // signed-in admin's token, so only login goes without it.
                if (token && !config.url?.includes("/login")) {
                    config.headers["Authorization"] = `Bearer ${token}`;
                }

                // Common headers
                config.headers["Timezone"] = timeZone;
                config.headers["Accept-Language"] = locale;

                // Only set Content-Type for non-FormData requests
                // For FormData, let the browser set the correct multipart/form-data header
                if (!(config.data instanceof FormData)) {
                    config.headers["Content-Type"] = "application/json";
                }
            }

            // Debug logging (disable in production)
            if (process.env.NODE_ENV === "development") {
                // console.log("📡 API Request:", {
                //     url: config.baseURL + config.url,
                //     method: config.method?.toUpperCase(),
                //     headers: config.headers,
                //     data: config.data,
                // });
            }
        }
        return config;
    },
    (error) => Promise.reject(error)
);

// Pages usually report their own failures with something more specific ("Error adding
// brand"). The generic toast waits a beat and stays quiet if the page already showed an
// error, so one failure is one toast.
export const PAGE_ERROR_GRACE_MS = 300;
const reportUnlessPageDid = (message) => {
    const failedAt = Date.now();
    setTimeout(() => {
        if (!errorToastShownSince(failedAt)) toast.error(message);
    }, PAGE_ERROR_GRACE_MS);
};

// Response Interceptor
axiosInstance.interceptors.response.use(
    (response) => response,
    (error) => {
        if (typeof window !== "undefined") {
            if (error.response) {
                const status = error.response.status;

                if (status === 401) {
                    // Auto logout on unauthorized
                    Cookies.remove("accruewise-token");
                    Cookies.remove("accruewise-user");
                    Cookies.remove("accruewise-remember");
                    toast.error("Session expired. Please log in again.");
                    // Optional: redirect to login
                    window.location.href = "/auth/login";
                } else if (status === 403) {
                    // Missing permission: keep the session, tell the user once,
                    // and let PermissionsProvider re-fetch (the role may have changed).
                    toast.error(
                        error.response.data?.message || "You don't have permission to do that.",
                        { toastId: "rbac-forbidden" }
                    );
                    window.dispatchEvent(new Event("accruewise:forbidden"));
                } else {
                    reportUnlessPageDid(
                        error.response.data?.message || "Something went wrong. Please try again."
                    );
                }
            } else {
                reportUnlessPageDid("Network error. Please check your connection.");
            }
        }
        return Promise.reject(error);
    }
);

export default axiosInstance;
